/**
 * New campaign's "Days to call" and "Calling hours" (owner, 2026-10-30): any number of ranges, the same every day or
 * different per day. The rules and the conversions are `./calling-hours.ts`'s; this file only draws them.
 *
 * **Same hours every day** keeps the earlier form — the day pills, then the ranges ("10:00 to 13:30", "Add hours" for
 * an evening range) used on each chosen day. **Different hours each day** lists Monday to Sunday, each with its own
 * ranges and "Add hours"; a day with none says "No calls this day", and "Copy to all calling days" copies the first
 * calling day's ranges, as the agent's opening hours do (`agents/[id]/channels/answering-panel.tsx`).
 *
 * The help assistant's fields (`day_of_week`, `from_local_time`, `to_local_time`) mark the day pills and the first
 * range's two times: the screen binds them only while the form is in **same** mode with one range.
 */
import { Button, Icon, Input, Segmented, classes, type SegmentedOption } from "@wetalk/ui";
import type { ReactNode } from "react";

import type { HelpFieldMark } from "../../help/fill/use-help-fillable.js";
import { GLYPH } from "../../shell/glyph.js";
import { portalText } from "../../i18n/portal.js";
import { CAMPAIGN_GLYPH } from "./campaign-glyph.js";
import { DAY_OPTION } from "./campaign-copy.js";
import {
  callingRangesOf,
  callingHoursProblemText,
  dayShortOf,
  nextRangeOf,
  withMode,
  type CallingHoursDraft,
  type CallingHoursMode,
  type RangeDraft,
} from "./calling-hours.js";
import { InfoTip } from "./info-tip.js";
import { TIME_ZONE_TIP, daysNoteOf } from "./new-campaign-plan.js";

const MODE_OPTION: readonly SegmentedOption<CallingHoursMode>[] = [
  { value: "same", get label() { return portalText("operations", "same_hours_every_day_c9decb35"); } },
  { value: "per_day", get label() { return portalText("operations", "different_hours_each_day_d6832dcb"); } },
];

/** What a help-fill mark spreads onto a control (the screen's `helpFill.mark(...)`). */
type Mark = HelpFieldMark;

export interface CallingHoursEditorProps {
  readonly draft: CallingHoursDraft;
  readonly onChange: (next: CallingHoursDraft) => void;
  readonly markDays: Mark;
  readonly markFrom: Mark;
  readonly markTo: Mark;
}

export function CallingHoursEditor({ draft, onChange, markDays, markFrom, markTo }: CallingHoursEditorProps): ReactNode {
  const problem = callingHoursProblemText(callingRangesOf(draft));
  const setDay = (isoDay: number, ranges: readonly RangeDraft[]): void => {
    onChange({ ...draft, perDay: draft.perDay.map((one, at) => (at === isoDay - 1 ? ranges : one)) });
  };
  const firstCallingDay = DAY_OPTION.find((one) => (draft.perDay[one.day - 1] ?? []).length > 0)?.day;

  return (
    <>
      <div className="field">
        <span className="c-camp-label">{portalText("operations", "calling_hours_6c75a87e")}</span>
        <Segmented
          name="campaignCallingHoursMode"
          legend={portalText("operations", "how_the_hours_repeat_74e14e91")}
          value={draft.mode}
          options={[...MODE_OPTION]}
          onChange={(mode) => {
            onChange(withMode(draft, mode));
          }}
          helpNever
        />
      </div>

      {draft.mode === "same" ? (
        <>
          <div className="field">
            <span className="c-camp-label">{portalText("operations", "days_to_call_1cffac1d")}</span>
            <div className="c-camp-days" role="group" aria-label={portalText("operations", "days_to_call_1cffac1d")} {...markDays}>
              {DAY_OPTION.map((one) => {
                const on = draft.days.includes(one.day);
                return (
                  <button
                    key={one.day}
                    type="button"
                    aria-pressed={on}
                    aria-label={`${one.label} · ${on ? portalText("operations", "campaign.day.calling") : portalText("operations", "not_calling_f5ba6324")}`}
                    className={classes("c-camp-day", on && "c-camp-day-on")}
                    onClick={() => {
                      onChange({ ...draft, days: on ? draft.days.filter((day) => day !== one.day) : [...draft.days, one.day] });
                    }}
                  >
                    <Icon glyph={on ? CAMPAIGN_GLYPH.check : CAMPAIGN_GLYPH.minus} size="xs" />
                    {one.label}
                  </button>
                );
              })}
            </div>
            <span className="c-camp-hint c-camp-hint-days">{daysNoteOf(draft.days)}</span>
          </div>
          <div className="field">
            <RangeList
              ranges={draft.ranges}
              startLabel={() => portalText("operations", "calling_starts_84ddffbb")}
              endLabel={() => portalText("operations", "calling_ends_ecd53da3")}
              markFrom={markFrom}
              markTo={markTo}
              onChange={(ranges) => {
                onChange({ ...draft, ranges });
              }}
            />
          </div>
        </>
      ) : (
        <div className="field">
          <div className="c-camp-week">
            {DAY_OPTION.map((one) => {
              const ranges = draft.perDay[one.day - 1] ?? [];
              return (
                <div className="c-camp-week-day" key={one.day}>
                  <span className="c-camp-week-label">{one.label}</span>
                  <div className="c-camp-week-ranges">
                    {ranges.length === 0 ? (
                      <span className="c-camp-week-off">{portalText("operations", "no_calls_this_day_4e6d0a2c")}</span>
                    ) : null}
                    <RangeList
                      ranges={ranges}
                      allowNone
                      startLabel={() => portalText("operations", "value_calling_starts_57f0ddeb", { v0: dayShortOf(one.day) })}
                      endLabel={() => portalText("operations", "value_calling_ends_7e505b13", { v0: dayShortOf(one.day) })}
                      onChange={(next) => {
                        setDay(one.day, next);
                      }}
                      extra={
                        one.day === firstCallingDay ? (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              onChange({
                                ...draft,
                                perDay: draft.perDay.map((held) => (held.length > 0 ? ranges : held)),
                              });
                            }}
                          >
                            <Icon glyph={GLYPH.copy} size="xs" />
                            {portalText("operations", "copy_to_all_calling_days_8acac7b8")}
                          </Button>
                        ) : null
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="field">
        <span className="c-camp-hint c-camp-hint-tip">
          {portalText("operations", "in_each_person_s_local_time_ef677fe8")}
          <InfoTip label={portalText("operations", "how_local_time_is_worked_out_ce18783d")}>{TIME_ZONE_TIP()}</InfoTip>
        </span>
        <span className="c-camp-hint">{portalText("operations", "add_as_many_ranges_as_you_need_like_a_morning_and_an_eveni_b81ee3e1")}</span>
        {problem === null ? null : (
          <span className="c-camp-hint c-note-critical" role="alert">
            {problem}
          </span>
        )}
      </div>
    </>
  );
}

interface RangeListProps {
  readonly ranges: readonly RangeDraft[];
  readonly onChange: (next: readonly RangeDraft[]) => void;
  readonly startLabel: () => string;
  readonly endLabel: () => string;
  /** A day of **per_day** may have none (no calls that day); **same** keeps at least one. */
  readonly allowNone?: boolean;
  readonly markFrom?: Mark;
  readonly markTo?: Mark;
  readonly extra?: ReactNode;
}

/** The ranges of one list — each "HH:MM to HH:MM" with a remove button — and "Add hours". */
function RangeList({ ranges, onChange, startLabel, endLabel, allowNone = false, markFrom, markTo, extra }: RangeListProps): ReactNode {
  const set = (at: number, next: RangeDraft): void => {
    onChange(ranges.map((one, each) => (each === at ? next : one)));
  };
  return (
    <div className="c-camp-ranges">
      {ranges.map((range, at) => (
        <span className="c-camp-hours" key={String(at)}>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            maxLength={5}
            aria-label={startLabel()}
            value={range.from}
            {...(at === 0 ? markFrom : undefined)}
            onChange={(event) => {
              set(at, { ...range, from: event.target.value });
            }}
          />
          <span className="c-camp-hint">{portalText("operations", "to_4374aaee")}</span>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            maxLength={5}
            aria-label={endLabel()}
            value={range.to}
            {...(at === 0 ? markTo : undefined)}
            onChange={(event) => {
              set(at, { ...range, to: event.target.value });
            }}
          />
          {allowNone || ranges.length > 1 ? (
            <button
              type="button"
              className="btn btn-icon c-camp-range-remove"
              aria-label={portalText("operations", "remove_these_hours_e8b32b6a")}
              onClick={() => {
                onChange(ranges.filter((_, each) => each !== at));
              }}
            >
              <Icon glyph={GLYPH.x} size="sm" />
            </button>
          ) : null}
        </span>
      ))}
      <span className="c-camp-range-acts">
        <Button
          variant="ghost"
          onClick={() => {
            onChange([...ranges, nextRangeOf(ranges)]);
          }}
        >
          <Icon glyph={GLYPH.plus} size="xs" />
          {portalText("operations", "add_hours_c4a82938")}
        </Button>
        {extra}
      </span>
    </div>
  );
}
